/**
 * Broker Registration Wizard Page
 * Step-based full-page flow: left stepper, center form, right reference panel.
 * Replaces the previous dialog-based registration.
 */

import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Collapse,
  IconButton,
  Alert,
  CircularProgress,
  Paper,
  Fade,
  Divider,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { FullPageWizardLayout } from '../../components/layout/FullPageWizardLayout';
import { getBrokerConfig } from './brokerRegistrationConfig';
import { ReferencePanel } from './ReferencePanel';
import { BrokerRegistrationWizardStep1, type WizardStep1Data } from './BrokerRegistrationWizardStep1';
import { BrokerRegistrationWizardStep2, type WizardStep2Data } from './BrokerRegistrationWizardStep2';
import {
  BrokerRegistrationWizardStep3,
  type WizardStep3Data,
} from './BrokerRegistrationWizardStep3';
import type { BrokerMetadata } from './types';
import { initiateBrokerRegistration, registerBrokerAccountWithTOTP } from '@shared/features/broker-accounts/services/brokerAccountService';
import { api } from '@shared/api/client';

const STEPS = ['User credentials', 'API credentials', 'Terms & Conditions'];

function getDefaultStep1Data(_broker: BrokerMetadata, config: ReturnType<typeof getBrokerConfig>): WizardStep1Data {
  const authModes = config?.authModes ?? ['oauth'];
  return {
    accountName: '',
    accountId: '',
    authMode: authModes.includes('oauth') ? 'oauth' : (authModes.includes('totp') ? 'totp' : 'oauth'),
  };
}

function getDefaultStep2Data(): WizardStep2Data {
  return { apiKey: '', apiSecret: '', showApiKey: false, showApiSecret: false, password: '', totpSecret: '' };
}


export function BrokerRegistrationWizardPage() {
  const { brokerType: brokerTypeParam } = useParams<{ brokerType: string }>();
  const navigate = useNavigate();
  const brokerType = (brokerTypeParam ?? '').toLowerCase().trim();

  const [broker, setBroker] = useState<BrokerMetadata | null>(null);
  const [loading, setLoading] = useState(true);
  const [redirectError, setRedirectError] = useState<string | null>(null);

  const [activeStep, setActiveStep] = useState(1);
  const [step1Data, setStep1Data] = useState<WizardStep1Data>({
    accountName: '',
    accountId: '',
    authMode: 'oauth',
  });
  const [step2Data, setStep2Data] = useState<WizardStep2Data>(getDefaultStep2Data());
  const [step3Data, setStep3Data] = useState<WizardStep3Data>({ accepted: false, hasViewedTC: false });
  const [stepError, setStepError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [totpSuccess, setTotpSuccess] = useState(false);
  const [referenceOpen, setReferenceOpen] = useState(true);

  const config = getBrokerConfig(brokerType);

  const fetchBroker = useCallback(async () => {
    if (!brokerType) return;
    setLoading(true);
    setRedirectError(null);
    try {
      const response = await api.brokerAccountsSupportedBrokers();
      const brokers: BrokerMetadata[] = response.data?.brokers ?? [];
      const found = brokers.find((b) => b.broker_type.toLowerCase() === brokerType);
      if (found) {
        setBroker(found);
        const cfg = getBrokerConfig(found.broker_type);
        setStep1Data(getDefaultStep1Data(found, cfg));
        setStep2Data(getDefaultStep2Data());
      } else {
        setRedirectError('Broker not found');
        setTimeout(() => navigate('/broker-accounts/add'), 2000);
      }
    } catch (err) {
      setRedirectError(err instanceof Error ? err.message : 'Failed to load broker details');
      setTimeout(() => navigate('/broker-accounts/add'), 2000);
    } finally {
      setLoading(false);
    }
  }, [brokerType, navigate]);

  useEffect(() => {
    fetchBroker();
  }, [fetchBroker]);

  const handleBack = () => {
    setStepError(null);
    setActiveStep((s) => Math.max(1, s - 1));
  };

  const validateStep1 = (): boolean => {
    if (!config) return false;
    if (!step1Data.accountName.trim()) {
      setStepError('Account name is required');
      return false;
    }
    if (!step1Data.accountId.trim()) {
      setStepError(config.accountIdLabel + ' is required');
      return false;
    }
    if (step1Data.accountId.trim().length < config.accountIdMinLength) {
      setStepError(
        config.accountIdLabel + ` must be at least ${config.accountIdMinLength} characters`
      );
      return false;
    }

    setStepError(null);
    return true;
  };

  const validateStep2 = (): boolean => {
    if (step1Data.authMode === 'totp') {
      const shortName =
        broker?.display_name?.split(' ')[0] ||
        (broker?.broker_type ? broker.broker_type.charAt(0).toUpperCase() + broker.broker_type.slice(1) : 'Broker');

      if (!step2Data.password?.trim()) {
        const passLabel =
          broker?.broker_type.toLowerCase() === 'dhan'
            ? 'Dhan Pin'
            : broker?.broker_type.toLowerCase() === 'groww'
              ? 'Groww TOTP Token'
              : `${shortName} Password`;
        setStepError(passLabel + ' is required');
        return false;
      }
      if (!step2Data.totpSecret?.trim()) {
        setStepError(`${shortName} TOTP Secret is required`);
        return false;
      }
      // Basic base32 validation for TOTP secret
      const cleaned = step2Data.totpSecret.replace(/\s/g, '').toUpperCase();
      if (cleaned.length < 16 || !/^[A-Z2-7]+$/.test(cleaned)) {
        setStepError(`${shortName} TOTP Secret must be at least 16 characters and in base32 format`);
        return false;
      }
    }

    if (!config?.requiresApiCredentials) return true;
    if (!step2Data.apiKey.trim()) {
      setStepError((config.apiKeyLabel ?? 'API Key') + ' is required');
      return false;
    }
    if (!step2Data.apiSecret.trim()) {
      setStepError((config.apiSecretLabel ?? 'API Secret') + ' is required');
      return false;
    }
    setStepError(null);
    return true;
  };

  const handleNext = () => {
    if (activeStep === 1 && !validateStep1()) return;
    if (activeStep === 2 && !validateStep2()) return;
    setStepError(null);
    setActiveStep((s) => Math.min(3, s + 1));
  };

  const handleConnectOAuth = async () => {
    if (!broker || !config) return;
    if (!validateStep1() || (config.requiresApiCredentials && !validateStep2())) return;

    setIsSubmitting(true);
    setStepError(null);
    try {
      const result = await initiateBrokerRegistration({
        broker_type: broker.broker_type as 'zerodha' | 'angelone' | 'upstox' | 'dhan' | 'groww' | 'fyers',
        account_name: step1Data.accountName.trim(),
        account_id: step1Data.accountId.trim(),
        paper_trading: false,
        real_time_data_enabled: true,
        auto_sync_enabled: true,
        ...(config.requiresApiCredentials
          ? { api_key: step2Data.apiKey.trim(), api_secret: step2Data.apiSecret.trim() }
          : {}),
      });
      if (result.authorization_url && typeof window !== 'undefined') {
        window.location.href = result.authorization_url;
      } else {
        setStepError('No authorization URL received from server');
      }
    } catch (err) {
      setStepError(err instanceof Error ? err.message : 'Failed to initiate broker registration');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConnectTOTP = async () => {
    if (!broker || !config) return;
    if (!validateStep1() || (config.requiresApiCredentials && !validateStep2())) return;

    setIsSubmitting(true);
    setStepError(null);
    try {
      const result = await registerBrokerAccountWithTOTP({
        broker_type: broker.broker_type,
        account_name: step1Data.accountName.trim(),
        account_id: step1Data.accountId.trim(),
        password: step2Data.password || '',
        totp_secret: (step2Data.totpSecret || '').replace(/\s/g, '').toUpperCase(),
        paper_trading: false,
        real_time_data_enabled: true,
        auto_sync_enabled: true,
        ...(config.requiresApiCredentials
          ? { api_key: step2Data.apiKey.trim(), api_secret: step2Data.apiSecret.trim() }
          : {}),
      });
      if (result.status === 'success' || result.broker_account_id) {
        setTotpSuccess(true);
      } else {
        setStepError(result.message || 'Failed to register account with TOTP');
      }
    } catch (err) {
      setStepError(err instanceof Error ? err.message : 'Failed to register account with TOTP');
    } finally {
      setIsSubmitting(false);
    }
  };


  if (loading) {
    return (
      <FullPageWizardLayout backTo="/broker-accounts/add">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <CircularProgress />
        </Box>
      </FullPageWizardLayout>
    );
  }

  if (redirectError || !broker) {
    return (
      <FullPageWizardLayout backTo="/broker-accounts/add">
        <Container maxWidth="sm" sx={{ py: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {redirectError ?? 'Broker not found'}
          </Alert>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/broker-accounts/add')}>
            Back to broker selection
          </Button>
        </Container>
      </FullPageWizardLayout>
    );
  }

  const displayName = broker.display_name || broker.broker_type;
  const authMode = step1Data.authMode;


  return (
    <FullPageWizardLayout backTo="/broker-accounts/add">
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Container maxWidth="xl" sx={{ py: { xs: 2, md: 5 }, px: { xs: 2, sm: 4 }, flex: 1, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
          <Box sx={{ mb: 4, flexShrink: 0 }}>
            <Typography variant="h5" fontWeight={800} color="#04377b" sx={{ mb: 1, letterSpacing: '-0.02em' }}>
              Connect {displayName}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.8 }}>
              Complete the steps below to securely link your broker account.
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '180px 1px 1fr 1px 1.1fr' },
              gap: 0,
              alignItems: 'stretch',
              flex: 1,
              height: '100%',
              minHeight: 0,
            }}
          >
            {/* Left: Stepper */}
            <Box
              sx={{
                p: { xs: 2, md: 4 },
                pl: { md: 0 },
                height: 'fit-content',
              }}
            >
              <Stepper
                activeStep={activeStep - 1}
                orientation="vertical"
                sx={{
                  '& .MuiStepLabel-root': { py: 1.5 },
                  '& .MuiStepLabel-label': {
                    fontWeight: 500,
                    fontSize: '0.8125rem',
                    color: 'text.secondary',
                    '&.Mui-active': { fontWeight: 700, color: '#04377b' },
                    '&.Mui-completed': { color: 'success.main' },
                  },
                  '& .MuiStepIcon-root': {
                    width: 28,
                    height: 28,
                    '&.Mui-active': { color: '#04377b' },
                    '&.Mui-completed': { color: 'success.main' },
                  }
                }}
              >
                {STEPS.map((label, index) => (
                  <Step key={label} completed={activeStep > index + 1 || (index === 2 && step3Data.accepted)}>
                    <StepLabel
                      StepIconProps={{
                        onClick: activeStep > index + 1 ? () => setActiveStep(index + 1) : undefined,
                        sx: activeStep > index + 1 ? { cursor: 'pointer' } : undefined,
                      }}
                    >
                      {label}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>

            {/* Vertical Divider 1 */}
            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' }, mx: 0, opacity: 0.6 }} />

            {/* Center: Step content */}
            <Box
              sx={{
                p: { xs: 2, md: 6 },
                display: 'flex',
                flexDirection: 'column',
                overflowY: 'auto',
                height: '100%',
                maxHeight: 'calc(100vh - 200px)',
                '&::-webkit-scrollbar': { width: '6px' },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  borderRadius: '10px',
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' },
                },
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 4,
                  border: '1px solid',
                  borderColor: 'rgba(0, 0, 0, 0.06)',
                  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.04)',
                  bgcolor: '#ffffff',
                  maxWidth: '100%',
                  width: '100%',
                  mx: 'auto',
                }}
              >
                {totpSuccess ? (
                  <Fade in={true} timeout={600}>
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <CheckCircleIcon sx={{ color: 'success.main', mb: 3, fontSize: 60 }} />
                      <Typography variant="h5" color="success.main" fontWeight={700} gutterBottom>
                        Account linked successfully
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 4, px: 2 }}>
                        Your {displayName} account is now connected. You can start using it for automated trading and portfolio management.
                      </Typography>
                      <Button
                        variant="contained"
                        size="large"
                        onClick={() => navigate('/broker-accounts')}
                        sx={{
                          textTransform: 'none',
                          px: 4,
                          borderRadius: 2,
                          boxShadow: '0 8px 24px rgba(4, 55, 123, 0.25)',
                        }}
                      >
                        Go to broker accounts
                      </Button>
                    </Box>
                  </Fade>
                ) : (
                  <Box>
                    <Fade in={true} key={activeStep} timeout={400}>
                      <Box>
                        {activeStep === 1 && config && (
                          <BrokerRegistrationWizardStep1
                            broker={broker}
                            data={step1Data}
                            onChange={setStep1Data}
                            authModes={config.authModes}
                            accountIdLabel={config.accountIdLabel}
                            accountIdPlaceholder={config.accountIdPlaceholder}
                            accountIdHelperText={config.accountIdHelperText}
                            accountIdMinLength={config.accountIdMinLength}
                            error={stepError ?? undefined}
                          />
                        )}
                        {activeStep === 2 && config && (
                          <BrokerRegistrationWizardStep2
                            broker={broker}
                            data={step2Data}
                            onChange={setStep2Data}
                            requiresApiCredentials={config.requiresApiCredentials}
                            authMode={step1Data.authMode}
                            apiKeyLabel={config.apiKeyLabel}
                            apiSecretLabel={config.apiSecretLabel}
                            apiKeyHelperText={config.apiKeyHelperText}
                            apiSecretHelperText={config.apiSecretHelperText}
                            step2NotRequiredCopy={config.step2NotRequiredCopy}
                            error={stepError ?? undefined}
                          />
                        )}
                        {activeStep === 3 && config && (
                          <BrokerRegistrationWizardStep3
                            broker={broker}
                            step1Data={step1Data}
                            data={step3Data}
                            onChange={setStep3Data}
                            error={stepError}
                          />
                        )}

                      </Box>
                    </Fade>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 5, pt: 3, borderTop: '1px solid rgba(0, 0, 0, 0.05)' }}>
                      <Button
                        disabled={activeStep === 1 || isSubmitting}
                        onClick={handleBack}
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          visibility: activeStep === 1 ? 'hidden' : 'visible',
                          bgcolor: 'rgba(4, 55, 123, 0.08)',
                          color: '#04377b',
                          boxShadow: 'none',
                          '&:hover': {
                            bgcolor: '#04377b',
                            color: '#fff',
                            boxShadow: '0 4px 12px rgba(4, 55, 123, 0.2)',
                          },
                          '&.Mui-disabled': {
                            bgcolor: 'rgba(0, 0, 0, 0.04)',
                            color: 'rgba(0, 0, 0, 0.26)',
                          }
                        }}
                      >
                        Back
                      </Button>
                      {activeStep < 3 ? (
                        <Button
                          variant="contained"
                          onClick={handleNext}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 4,
                            borderRadius: 1.5,
                            boxShadow: '0 8px 20px rgba(4, 55, 123, 0.15)',
                          }}
                        >
                          Continue
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          onClick={authMode === 'oauth' ? handleConnectOAuth : handleConnectTOTP}
                          disabled={isSubmitting || !step3Data.accepted}
                          startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : undefined}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 4,
                            borderRadius: 1.5,
                            boxShadow: '0 8px 20px rgba(4, 55, 123, 0.15)',
                            opacity: !step3Data.accepted && !isSubmitting ? 0.6 : 1,
                          }}
                        >
                          {isSubmitting ? 'Connecting…' : 'Proceed to Connect'}
                        </Button>
                      )}
                    </Box>
                  </Box>
                )}
              </Paper>
            </Box>

            {/* Vertical Divider 2 */}
            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' }, mx: 0, opacity: 0.6 }} />

            {/* Right: Reference panel */}
            <Box
              sx={{
                p: { xs: 2, md: 4 },
                pr: { md: 0 },
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                maxHeight: 'calc(100vh - 200px)',
              }}
            >
              <Box
                sx={{
                  display: { xs: 'flex', md: 'none' },
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 2,
                  flexShrink: 0,
                }}
              >
                <Typography variant="subtitle2" fontWeight={700} color="#04377b">
                  Help & Guidance
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setReferenceOpen(!referenceOpen)}
                  aria-expanded={referenceOpen}
                  sx={{ bgcolor: 'rgba(4, 55, 123, 0.05)' }}
                >
                  {referenceOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
              <Collapse in={referenceOpen} sx={{ display: { xs: 'block', md: 'none' }, flexShrink: 0 }}>
                <ReferencePanel brokerType={brokerType} stepIndex={activeStep} compact />
              </Collapse>
              <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', flex: 1, minHeight: 0 }}>
                <Typography variant="subtitle2" fontWeight={700} color="#04377b" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.75rem', flexShrink: 0 }}>
                  Help & Guidance
                </Typography>
                <Box
                  sx={{
                    flex: 1,
                    overflowY: 'auto',
                    minHeight: 0,
                    pr: 1,
                    '&::-webkit-scrollbar': { width: '6px' },
                    '&::-webkit-scrollbar-track': { background: 'transparent' },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      borderRadius: '10px',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' },
                    },
                  }}
                >
                  <ReferencePanel brokerType={brokerType} stepIndex={activeStep} />
                </Box>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
    </FullPageWizardLayout>
  );
}
